import { Injectable } from '@angular/core';
import { LanguageService } from './language.service';
import { VehicleColorsService } from './vehicle-colors.service';
import { VehicleUtils } from '../utils/vehicle.utils';
import autoTable from 'jspdf-autotable';
import jsPDF from 'jspdf';

@Injectable({
  providedIn: 'root'
})
export class PdfGeneratorService {

  margin: number = 20;
  hHeader: number = 50;
  footerSpace: number = 30;
  yInizioDopoHeader: number = 60;

  constructor(
    private languageService: LanguageService,
    private vehicleColorsService: VehicleColorsService
  ) { }

  async generateCrashReportAsBlob(data: any, screenshots3D?: string[], userPhotos?: string[], screenshotNames?: string[]): Promise<Blob> {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    try {
      const imgLoadPromise = new Promise<HTMLImageElement | null>((resolve) => {
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = 'assets/logo.png';
      });

      const logoImg = await imgLoadPromise;
      return await this.generatePDFBlobWithLogo(data, logoImg, screenshots3D, userPhotos, screenshotNames);
    } catch (error) {
      console.error('Error generating PDF blob:', error);
      throw error;
    }
  }

  private async generatePDFBlobWithLogo(data: any, logoImg: HTMLImageElement | null, screenshots3D?: string[], userPhotos?: string[], screenshotNames?: string[]): Promise<Blob> {
    
    const doc = new jsPDF();
    
    doc.setFont('helvetica', 'normal');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - 2 * this.margin;

    doc.setFillColor(30, 58, 138);
    doc.rect(0, 0, pageWidth, this.hHeader, 'F');
    this.addLogoToPDF(doc, 0, 8, 60, 15, logoImg);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    const title = this.sanitizeTextForPDF(this.languageService.translate('pdf.title'));
    doc.text(title, pageWidth / 2, 40, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    let yPos = this.yInizioDopoHeader;

    yPos = this.addTableSection(doc, 
      this.sanitizeTextForPDF(this.languageService.translate('pdf.generalData')), 
      [
        [this.sanitizeTextForPDF(this.languageService.translate('summary.date')), this.sanitizeTextForPDF(data.formattedDate)],
        [this.sanitizeTextForPDF(this.languageService.translate('summary.time')), this.sanitizeTextForPDF(data.formattedTime)],
        [this.sanitizeTextForPDF(this.languageService.translate('summary.place')), this.sanitizeTextForPDF(data.address || this.languageService.translate('summary.notSpecified'))],
        [this.sanitizeTextForPDF(this.languageService.translate('injuries.presence')), this.sanitizeTextForPDF(data.hasInjuries ? this.languageService.translate('common.yes') : this.languageService.translate('common.no'))],
        [this.sanitizeTextForPDF(this.languageService.translate('damage.material.beyond.vehicles')), this.sanitizeTextForPDF(data.hasMaterialDamage ? this.languageService.translate('common.yes') : this.languageService.translate('common.no'))],
        [this.sanitizeTextForPDF(this.languageService.translate('witnesses.presence')), this.sanitizeTextForPDF(data.hasWitnesses ? this.languageService.translate('common.yes') : this.languageService.translate('common.no'))]
      ], 
      yPos, '#6b7280');

    const vehicleAData = [
      [this.sanitizeTextForPDF(this.languageService.translate('pdf.driverName')), this.sanitizeTextForPDF(data.driverName || '')],
      [this.sanitizeTextForPDF(this.languageService.translate('pdf.phone')), this.sanitizeTextForPDF(data.driverPhone || '')],
      [this.sanitizeTextForPDF(this.languageService.translate('pdf.plate')), this.sanitizeTextForPDF(data.vehiclePlate || '')]
    ];
    
    if (data.driverEmail && data.driverEmail.trim()) {
      vehicleAData.push([this.sanitizeTextForPDF(this.languageService.translate('pdf.email')), this.sanitizeTextForPDF(data.driverEmail)]);
    }
    if (data.vehicleBrand && data.vehicleBrand.trim()) {
      vehicleAData.push([this.sanitizeTextForPDF(this.languageService.translate('pdf.brand')), this.sanitizeTextForPDF(data.vehicleBrand)]);
    }
    if (data.vehicleModel && data.vehicleModel.trim()) {
      vehicleAData.push([this.sanitizeTextForPDF(this.languageService.translate('pdf.model')), this.sanitizeTextForPDF(data.vehicleModel)]);
    }
    
    let vehicleADynamics = data.accidentDynamics;
    
    if (!vehicleADynamics && data.involvedVehiclesWithDynamics) {
      const vehicleA = data.involvedVehiclesWithDynamics.find((v: any) => v.letter === 'A');
      if (vehicleA && vehicleA.dynamics) {
        vehicleADynamics = vehicleA.dynamics;
      }
    }
    
    if (vehicleADynamics && vehicleADynamics.length > 0) {
      const translatedDynamics = vehicleADynamics.map((dynamic: string) => 
        this.sanitizeTextForPDF(this.languageService.translate(dynamic))
      );
      vehicleAData.push([
        this.sanitizeTextForPDF(this.languageService.translate('pdf.accidentDynamics')), 
        translatedDynamics.join(', ')
      ]);
    }
    
    yPos = this.addTableSection(doc, 
      this.sanitizeTextForPDF(this.languageService.translate('pdf.vehicleA')), 
      vehicleAData, 
      yPos, 'rgb(29, 78, 216)');

    if (data.involvedVehiclesWithDynamics?.length > 0) {
      const vehicleColors = this.vehicleColorsService.generateColorsForVehicles(data.involvedVehiclesWithDynamics.length + 1);
      const vehicleLetters = VehicleUtils.getVehicleLetters();
      
      data.involvedVehiclesWithDynamics.forEach((vehicle: any, index: number) => {
        const vehicleLetter = vehicleLetters[index + 1];
        const vehicleData = [
          [this.sanitizeTextForPDF(this.languageService.translate('pdf.plate')), this.sanitizeTextForPDF(vehicle.plate || this.languageService.translate('summary.notSpecified'))]
        ];

        if (vehicle.nome?.trim()) {
          vehicleData.push([this.sanitizeTextForPDF(this.languageService.translate('field.nome')), this.sanitizeTextForPDF(vehicle.nome)]);
        }
        if (vehicle.cognome?.trim()) {
          vehicleData.push([this.sanitizeTextForPDF(this.languageService.translate('field.cognome')), this.sanitizeTextForPDF(vehicle.cognome)]);
        }
        if (vehicle.indirizzo?.trim()) {
          vehicleData.push([this.sanitizeTextForPDF(this.languageService.translate('field.indirizzoResidenza')), this.sanitizeTextForPDF(vehicle.indirizzo)]);
        }
        if (vehicle.telefono?.trim()) {
          vehicleData.push([this.sanitizeTextForPDF(this.languageService.translate('field.telefono')), this.sanitizeTextForPDF(vehicle.telefono)]);
        }
        
        if (vehicle.dynamics?.length > 0) {
          const translatedDynamics = vehicle.dynamics.map((dynamic: string) => 
            this.sanitizeTextForPDF(this.languageService.translate(dynamic))
          );
          vehicleData.push([
            this.sanitizeTextForPDF(this.languageService.translate('pdf.accidentDynamics')), 
            translatedDynamics.join(', ')
          ]);
        }
        
        const vehicleColor = vehicleColors[`vehicle${vehicleLetter}`];
        const colorString = `rgb(${Math.round(parseInt(vehicleColor.color.slice(1, 3), 16))}, ${Math.round(parseInt(vehicleColor.color.slice(3, 5), 16))}, ${Math.round(parseInt(vehicleColor.color.slice(5, 7), 16))})`;
        
        yPos = this.addTableSection(doc, 
          `${this.sanitizeTextForPDF(this.languageService.translate('pdf.vehicle'))} ${vehicleLetter}`, 
          vehicleData, 
          yPos, colorString);
      });
    }

    if (data.witnesses && data.witnesses.length > 0) {
      yPos = this.addWitnessesSection(doc, data.witnesses, yPos);
    }

    if (data.witnessDocumentPhotos?.length > 0) {
      yPos = await this.addWitnessDocumentPhotos(doc, data.witnessDocumentPhotos, yPos, contentWidth, this.margin);
    }

    if (screenshots3D && screenshots3D.length > 0) {
      yPos = await this.add3DScreenshots(doc, screenshots3D, yPos, contentWidth, this.margin, screenshotNames);
    }
    
    const vehicleLetters = VehicleUtils.getVehicleLetters();
    for (let i = 0; i < vehicleLetters.length; i++) {
      const vehicleLetter = vehicleLetters[i];
      let vehiclePhotos: string[] = [];
      
      // Cerca in data[`vehicle${letter}Photos`]
      if (data[`vehicle${vehicleLetter}Photos`] && data[`vehicle${vehicleLetter}Photos`].length > 0) {
        vehiclePhotos = data[`vehicle${vehicleLetter}Photos`];
      }
      
      if (!vehiclePhotos.length && data.allVehiclePhotos && typeof data.allVehiclePhotos === 'object') {
        if (data.allVehiclePhotos[vehicleLetter] && Array.isArray(data.allVehiclePhotos[vehicleLetter])) {
          vehiclePhotos = data.allVehiclePhotos[vehicleLetter];
        }
      }
      
      if (vehiclePhotos && vehiclePhotos.length > 0) {
        yPos = await this.addVehiclePhotos(doc, vehiclePhotos, yPos, contentWidth, this.margin, vehicleLetter);
      }
    }
    
    if (userPhotos && userPhotos.length > 0) {
      yPos = await this.addUserPhotos(doc, userPhotos, yPos, contentWidth, this.margin);
    }

    this.addFooter(doc, pageWidth, pageHeight, this.margin);
    return doc.output('blob');
  }

  private addTableSection(doc: jsPDF, title: string, data: string[][], yPos: number, headerColor: string): number {
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;

    if (yPos + 20 > pageHeight - this.footerSpace) {
      doc.addPage();
      this.addSimpleHeader(doc, pageWidth);
      yPos = this.hHeader + 5;
    }

    const color = this.parseColor(headerColor);

    const tableConfig: any = {
      startY: yPos,
      head: [[title, '']],
      body: data,
      theme: 'grid',
      tableWidth: 'auto',
      margin: { left: margin, right: margin },
      headStyles: {
        fillColor: [color[0], color[1], color[2]],
        textColor: [255, 255, 255],
        fontSize: 11,
        fontStyle: 'bold',
        halign: 'left',
        cellPadding: { top: 4, right: 6, bottom: 4, left: 6 },
        minCellHeight: 10
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: { top: 3, right: 6, bottom: 3, left: 6 },
        valign: 'top',
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        minCellHeight: 8
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      columnStyles: {
        0: {
          cellWidth: 60,
          fontStyle: 'bold',
          textColor: [75, 85, 99],
          halign: 'left',
          valign: 'top'
        },
        1: {
          cellWidth: pageWidth - margin * 2 - 60,
          textColor: [31, 41, 55],
          halign: 'left',
          valign: 'top'
        }
      },
      styles: {
        overflow: 'linebreak',
        cellWidth: 'wrap',
        fontSize: 9,
        cellPadding: 3
      },
      pageBreak: 'avoid',
      showHead: 'firstPage',
      tableLineColor: [200, 200, 200],
      tableLineWidth: 0.1,
      didDrawPage: (data: any) => {
        if (data.cursor) {
          data.cursor.y = Math.max(data.cursor.y, this.hHeader + 10);
        }
      },
      willDrawCell: (data: any) => {
        if (data.section === 'head' && data.pageNumber > 1) {
          return false;
        }
        return true;
      }
    };

    autoTable(doc, tableConfig);
    return (doc as any).lastAutoTable.finalY + 5;
  }

  private parseColor(color: string): number[] {
    if (color.startsWith('rgb(')) {
      const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      return match ? [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])] : [128, 128, 128];
    }
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
    }
    return [128, 128, 128];
  }

  private addSimpleHeader(doc: jsPDF, pageWidth: number): void {
    const headerHeight = 30;

    doc.setFillColor(30, 58, 138);
    doc.rect(0, 0, pageWidth, headerHeight, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(this.sanitizeTextForPDF(this.languageService.translate('pdf.title')), pageWidth / 2, headerHeight / 2 + 4, { align: 'center' });

    const now = new Date();
    const dateStr = now.toLocaleDateString('it-IT');
    const timeStr = now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    doc.setFontSize(7);
    doc.text(`${dateStr} ${timeStr}`, pageWidth - 10, 10, { align: 'right' });
  }

  private addLogoToPDF(doc: jsPDF, x: number, y: number, width: number, height: number, logoImg: HTMLImageElement | null): void {
    const pageWidth = doc.internal.pageSize.getWidth();
    const centeredX = (pageWidth - width) / 2;

    doc.setFillColor(255, 255, 255);
    doc.roundedRect(centeredX, y, width, height, 3, 3, 'F');

    if (logoImg) {
      doc.addImage(logoImg, 'PNG', centeredX + 3, y + 3, width - 6, height - 6);
    } else {
      doc.setTextColor(30, 58, 138);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('TRACKYCONTROL', pageWidth / 2, y + height / 2 + 2, { align: 'center' });
    }
  }

  // Funzione helper per calcolare le dimensioni mantenendo l'aspect ratio
  private calculateImageDimensions(
    originalWidth: number, 
    originalHeight: number, 
    maxWidth: number, 
    maxHeight: number
  ): { width: number, height: number } {
    const aspectRatio = originalWidth / originalHeight;
    
    let newWidth = maxWidth;
    let newHeight = maxWidth / aspectRatio;
    
    if (newHeight > maxHeight) {
      newHeight = maxHeight;
      newWidth = maxHeight * aspectRatio;
    }
    
    return { width: newWidth, height: newHeight };
  }

  // Funzione helper per ottenere le dimensioni di un'immagine base64
  private getImageDimensions(base64String: string): Promise<{ width: number, height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      img.src = base64String;
    });
  }

  private async add3DScreenshots(doc: jsPDF, screenshots: string[], yPos: number, contentWidth: number, margin: number, names?: string[]): Promise<number> {
    const pageHeight = doc.internal.pageSize.getHeight();
    const maxYPos = pageHeight - 40;
    const imageSize = 70; 
    const imagesPerRow = 2;
    const maxImageWidth = (contentWidth - 10) / imagesPerRow;

    if (yPos + 10 > maxYPos) {
      doc.addPage();
      this.addSimpleHeader(doc, doc.internal.pageSize.getWidth());
      yPos = this.hHeader;
    }

    doc.setFillColor(75, 85, 99);
    doc.rect(margin, yPos, contentWidth, 14, 'F');

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(this.sanitizeTextForPDF(this.languageService.translate('pdf.3dViews')), margin + 8, yPos + 10);

    yPos += 16;
    let currentRowY = yPos;
    let maxRowHeight = 0;

    for (let index = 0; index < screenshots.length; index++) {
      const screenshot = screenshots[index];
      const col = index % imagesPerRow;
      const xPos = margin + (col * (maxImageWidth + 5));

      try {
        // Controlla se serve una nuova pagina
        if (col === 0 && currentRowY + imageSize + 20 > maxYPos) {
          doc.addPage();
          this.addSimpleHeader(doc, doc.internal.pageSize.getWidth());
          currentRowY = this.hHeader + 10;
          maxRowHeight = 0;
        }

        // Centra l'immagine nel suo spazio allocato
        const centeredX = xPos + (maxImageWidth - imageSize) / 2;

        // Aggiungi l'immagine con dimensioni quadrate fisse
        doc.addImage(screenshot, 'PNG', centeredX, currentRowY, imageSize, imageSize);
        
        // Usa la dimensione quadrata fissa per l'altezza della riga
        maxRowHeight = Math.max(maxRowHeight, imageSize);

        // Se è l'ultima immagine della riga o l'ultima immagine in assoluto
        if (col === imagesPerRow - 1 || index === screenshots.length - 1) {
          currentRowY += maxRowHeight + 20;
          maxRowHeight = 0;
        }
      } catch (error) {
        console.error('Errore nell\'aggiungere screenshot 3D:', error);
        
        // In caso di errore, usa la dimensione quadrata fissa
        if (col === imagesPerRow - 1 || index === screenshots.length - 1) {
          currentRowY += imageSize + 20;
          maxRowHeight = 0;
        }
      }
    }

    doc.setTextColor(0, 0, 0);
    return currentRowY + 10;
  }
  
  private async addVehiclePhotos(doc: jsPDF, photos: string[], yPos: number, contentWidth: number, margin: number, vehicle: string): Promise<number> {
    const pageHeight = doc.internal.pageSize.getHeight();
    const maxYPos = pageHeight - 40;
    const maxImageHeight = 70;
    const imagesPerRow = 3;
    const maxImageWidth = (contentWidth - 20) / imagesPerRow;

    if (yPos + 30 > maxYPos) {
      doc.addPage();
      this.addSimpleHeader(doc, doc.internal.pageSize.getWidth());
      yPos = this.hHeader + 10;
    }

    const vehicleColors = this.vehicleColorsService.generateColorsForVehicles(26);
    const vehicleColorScheme = vehicleColors[`vehicle${vehicle}`] || vehicleColors['vehicleA'];
    const colorHex = vehicleColorScheme.color;
    const color = [
      parseInt(colorHex.slice(1, 3), 16),
      parseInt(colorHex.slice(3, 5), 16), 
      parseInt(colorHex.slice(5, 7), 16)
    ];
    doc.setFillColor(color[0], color[1], color[2]);
    doc.rect(margin, yPos, contentWidth, 14, 'F');

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    const photoTitle = `${this.languageService.translate('pdf.photosVehicle')} ${vehicle}`;
    doc.text(this.sanitizeTextForPDF(photoTitle), margin + 8, yPos + 10);

    yPos += 20;
    let currentRowY = yPos;
    let maxRowHeight = 0;

    for (let index = 0; index < photos.length; index++) {
      const photo = photos[index];
      const col = index % imagesPerRow;
      const xPos = margin + (col * (maxImageWidth + 10));

      try {
        const originalDimensions = await this.getImageDimensions(photo);
        const newDimensions = this.calculateImageDimensions(
          originalDimensions.width,
          originalDimensions.height,
          maxImageWidth,
          maxImageHeight
        );

        if (col === 0 && currentRowY + newDimensions.height > maxYPos) {
          doc.addPage();
          this.addSimpleHeader(doc, doc.internal.pageSize.getWidth());
          currentRowY = this.hHeader + 10;
          maxRowHeight = 0;
        }

        const centeredX = xPos + (maxImageWidth - newDimensions.width) / 2;
        doc.addImage(photo, 'JPEG', centeredX, currentRowY, newDimensions.width, newDimensions.height);
        maxRowHeight = Math.max(maxRowHeight, newDimensions.height);

        if (col === imagesPerRow - 1 || index === photos.length - 1) {
          currentRowY += maxRowHeight + 15;
          maxRowHeight = 0;
        }
      } catch (error) {
        console.error(`Errore nell'aggiungere foto veicolo ${vehicle}:`, error);
        if (col === imagesPerRow - 1 || index === photos.length - 1) {
          currentRowY += maxImageHeight + 15;
          maxRowHeight = 0;
        }
      }
    }

    doc.setTextColor(0, 0, 0);
    return currentRowY + 10;
  }

  private async addUserPhotos(doc: jsPDF, photos: string[], yPos: number, contentWidth: number, margin: number): Promise<number> {
    const pageHeight = doc.internal.pageSize.getHeight();
    const maxYPos = pageHeight - 40;
    const maxImageHeight = 70; // Altezza massima per immagine
    const imagesPerRow = 3;
    const maxImageWidth = (contentWidth - 20) / imagesPerRow;

    if (yPos + 30 > maxYPos) {
      doc.addPage();
      this.addSimpleHeader(doc, doc.internal.pageSize.getWidth());
      yPos = this.hHeader + 10;
    }

    doc.setFillColor(34, 197, 94);
    doc.rect(margin, yPos, contentWidth, 14, 'F');

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Foto dei Danni', margin + 8, yPos + 10);

    yPos += 20;
    let currentRowY = yPos;
    let maxRowHeight = 0;

    for (let index = 0; index < photos.length; index++) {
      const photo = photos[index];
      const col = index % imagesPerRow;
      const xPos = margin + (col * (maxImageWidth + 10));

      try {
        // Ottieni le dimensioni originali dell'immagine
        const originalDimensions = await this.getImageDimensions(photo);
        
        // Calcola le nuove dimensioni mantenendo l'aspect ratio
        const newDimensions = this.calculateImageDimensions(
          originalDimensions.width,
          originalDimensions.height,
          maxImageWidth,
          maxImageHeight
        );

        // Controlla se serve una nuova pagina
        if (col === 0 && currentRowY + newDimensions.height > maxYPos) {
          doc.addPage();
          this.addSimpleHeader(doc, doc.internal.pageSize.getWidth());
          currentRowY = this.hHeader + 10;
          maxRowHeight = 0;
        }

        // Centra l'immagine nel suo spazio allocato se è più piccola
        const centeredX = xPos + (maxImageWidth - newDimensions.width) / 2;

        doc.addImage(photo, 'JPEG', centeredX, currentRowY, newDimensions.width, newDimensions.height);
        
        // Tieni traccia dell'altezza massima in questa riga
        maxRowHeight = Math.max(maxRowHeight, newDimensions.height);

        // Se è l'ultima immagine della riga o l'ultima immagine in assoluto
        if (col === imagesPerRow - 1 || index === photos.length - 1) {
          currentRowY += maxRowHeight + 15;
          maxRowHeight = 0;
        }
      } catch (error) {
        console.error('Errore nell\'aggiungere foto utente:', error);
        
        // In caso di errore, usa dimensioni di fallback
        if (col === imagesPerRow - 1 || index === photos.length - 1) {
          currentRowY += maxImageHeight + 15;
          maxRowHeight = 0;
        }
      }
    }

    doc.setTextColor(0, 0, 0);
    return currentRowY + 10;
  }

  private async addWitnessDocumentPhotos(
    doc: jsPDF,
    photos: string[],
    yPos: number,
    contentWidth: number,
    margin: number
  ): Promise<number> {
    const pageHeight = doc.internal.pageSize.getHeight();
    const maxYPos = pageHeight - 40;
    const maxImageHeight = 70;
    const imagesPerRow = 3;
    const maxImageWidth = (contentWidth - 20) / imagesPerRow;

    if (yPos + 30 > maxYPos) {
      doc.addPage();
      this.addSimpleHeader(doc, doc.internal.pageSize.getWidth());
      yPos = this.hHeader + 10;
    }

    doc.setFillColor(97, 97, 97);
    doc.rect(margin, yPos, contentWidth, 14, 'F');

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(
      this.sanitizeTextForPDF(this.languageService.translate('witnesses.document.title')),
      margin + 8,
      yPos + 10
    );

    yPos += 20;
    let currentRowY = yPos;
    let maxRowHeight = 0;

    for (let index = 0; index < photos.length; index++) {
      const photo = photos[index];
      const col = index % imagesPerRow;
      const xPos = margin + (col * (maxImageWidth + 10));

      try {
        const originalDimensions = await this.getImageDimensions(photo);
        const newDimensions = this.calculateImageDimensions(
          originalDimensions.width,
          originalDimensions.height,
          maxImageWidth,
          maxImageHeight
        );

        if (col === 0 && currentRowY + newDimensions.height > maxYPos) {
          doc.addPage();
          this.addSimpleHeader(doc, doc.internal.pageSize.getWidth());
          currentRowY = this.hHeader + 10;
          maxRowHeight = 0;
        }

        const centeredX = xPos + (maxImageWidth - newDimensions.width) / 2;
        doc.addImage(
          photo,
          this.getImageFormat(photo),
          centeredX,
          currentRowY,
          newDimensions.width,
          newDimensions.height
        );
        maxRowHeight = Math.max(maxRowHeight, newDimensions.height);

        if (col === imagesPerRow - 1 || index === photos.length - 1) {
          currentRowY += maxRowHeight + 15;
          maxRowHeight = 0;
        }
      } catch (error) {
        console.error('Errore nell\'aggiungere documento testimone al PDF:', error);
        if (col === imagesPerRow - 1 || index === photos.length - 1) {
          currentRowY += maxImageHeight + 15;
          maxRowHeight = 0;
        }
      }
    }

    doc.setTextColor(0, 0, 0);
    return currentRowY + 10;
  }

  private getImageFormat(dataUrl: string): 'JPEG' | 'PNG' {
    return dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
  }

  private addWitnessesSection(doc: jsPDF, witnesses: any[], yPos: number): number {
    for (let i = 0; i < witnesses.length; i++) {
      const witness = witnesses[i];
      const witnessData = [
        [this.sanitizeTextForPDF(this.languageService.translate('summary.name')), this.sanitizeTextForPDF(witness.nome || this.languageService.translate('summary.notSpecified'))],
        [this.sanitizeTextForPDF(this.languageService.translate('summary.surname')), this.sanitizeTextForPDF(witness.cognome || this.languageService.translate('summary.notSpecified'))],
        [this.sanitizeTextForPDF(this.languageService.translate('field.email')), this.sanitizeTextForPDF(witness.email || this.languageService.translate('summary.notSpecified'))],
        [this.sanitizeTextForPDF(this.languageService.translate('field.telefono')), this.sanitizeTextForPDF(witness.telefono || this.languageService.translate('summary.notSpecified'))]
      ];
      
      if (witness.dichiarazione) {
        witnessData.push([
          this.sanitizeTextForPDF(this.languageService.translate('witnesses.statement')), 
          this.sanitizeTextForPDF(witness.dichiarazione)
        ]);
      }
      
      const witnessTitle = witnesses.length === 1 
        ? this.sanitizeTextForPDF(this.languageService.translate('witnesses.single'))
        : `${this.sanitizeTextForPDF(this.languageService.translate('witnesses.single'))} ${i + 1}`;
      
      yPos = this.addTableSection(doc, 
        witnessTitle, 
        witnessData, 
        yPos, '#6b7280');
    }
    return yPos;
  }

  private addFooter(doc: jsPDF, pageWidth: number, pageHeight: number, margin: number): void {
    const totalPages = doc.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.line(margin, pageHeight - 25, pageWidth - margin, pageHeight - 25);

      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128);
      doc.setFont('helvetica', 'normal');
      doc.text(`Trackycontrol - ${this.sanitizeTextForPDF(this.languageService.translate('pdf.systemName'))}`, margin, pageHeight - 15);

      doc.setFont('helvetica', 'normal');
      doc.text(`${this.sanitizeTextForPDF(this.languageService.translate('pdf.page'))} ${i} di ${totalPages}`, pageWidth - margin, pageHeight - 15, { align: 'right' });
    }
  }

  private sanitizeTextForPDF(text: string): string {
    if (!text) return '';
    
    const cyrillicToLatin: { [key: string]: string } = {
      'А': 'A', 'а': 'a', 'Б': 'B', 'б': 'b', 'В': 'V', 'в': 'v',
      'Г': 'G', 'г': 'g', 'Д': 'D', 'д': 'd', 'Е': 'E', 'е': 'e',
      'Ж': 'Zh', 'ж': 'zh', 'З': 'Z', 'з': 'z', 'И': 'I', 'и': 'i',
      'Й': 'Y', 'й': 'y', 'К': 'K', 'к': 'k', 'Л': 'L', 'л': 'l',
      'М': 'M', 'м': 'm', 'Н': 'N', 'н': 'n', 'О': 'O', 'о': 'o',
      'П': 'P', 'п': 'p', 'Р': 'R', 'р': 'r', 'С': 'S', 'с': 's',
      'Т': 'T', 'т': 't', 'У': 'U', 'у': 'u', 'Ф': 'F', 'ф': 'f',
      'Х': 'H', 'х': 'h', 'Ц': 'Ts', 'ц': 'ts', 'Ч': 'Ch', 'ч': 'ch',
      'Ш': 'Sh', 'ш': 'sh', 'Щ': 'Sht', 'щ': 'sht', 'Ъ': 'A', 'ъ': 'a',
      'Ь': '', 'ь': '', 'Ю': 'Yu', 'ю': 'yu', 'Я': 'Ya', 'я': 'ya'
    };

    let sanitized = text.replace(/[\u0400-\u04FF]/g, (char) => cyrillicToLatin[char] || char);
    sanitized = sanitized.replace(/[^\x00-\x7F\u00C0-\u00FF]/g, '');
    
    return sanitized;
  }
}